import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { useUIBreakpoint, type Breakpoint } from '@/hooks/use-ui-breakpoint'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from './ide-v2/coogo-ide-chrome-copy'
import { cn } from '@/lib/utils'

export interface ResponsivePanelProps {
  id: 'inspector' | 'chat' | 'devtools'
  side: 'left' | 'right' | 'bottom'
  inlineFrom: Breakpoint
  modalFrom: Breakpoint
  open: boolean
  onOpenChange: (open: boolean) => void
  widthByBreakpoint?: Partial<Record<Breakpoint, string>>
  /**
   * Pixel size for the inline branch. Takes precedence over widthByBreakpoint.
   * `chat`/`inspector` interpret this as width, `devtools` as height. When
   * provided together with `onResize`, a drag handle is rendered on the
   * interior edge (opposite `side`) so the user can resize the panel.
   */
  size?: number
  onResize?: (size: number) => void
  className?: string
  sheetClassName?: string
  /**
   * F10 — OPTIONAL OVERRIDE ONLY. The panel names itself from the copy module
   * using its own `id`, so a call site that passes nothing still gets a
   * localized accessible name. Before this, `ChatDrawerSlot` passed none of
   * the three and the fallbacks were id-derived English ("chat panel",
   * "Resize chat panel") in every locale, and silent. `ChatRail` did pass
   * localized values, but its branch never renders (F2) — and it could not
   * have fixed the resize handle anyway, which accepted no label prop at all.
   */
  a11yTitle?: string
  a11yDescription?: string
  closeLabel?: string
  children: ReactNode
}

const BP_ORDER: Breakpoint[] = ['md', 'lg', 'xl', '2xl']
const RESIZE_STEP = 16
const RESIZE_LIMITS: Record<
  ResponsivePanelProps['id'],
  { min: number; max: number }
> = {
  chat: { min: 280, max: 480 },
  inspector: { min: 240, max: 640 },
  devtools: { min: 120, max: 600 },
}

function gte(a: Breakpoint, b: Breakpoint): boolean {
  return BP_ORDER.indexOf(a) >= BP_ORDER.indexOf(b)
}

export function ResponsivePanel({
  id,
  side,
  inlineFrom,
  modalFrom,
  open,
  onOpenChange,
  widthByBreakpoint,
  size,
  onResize,
  className,
  sheetClassName,
  a11yTitle,
  a11yDescription,
  closeLabel,
  children,
}: ResponsivePanelProps) {
  const { bp } = useUIBreakpoint()
  const { locale } = useStudioLocale()
  const panelCopy = COOGO_IDE_CHROME_COPY[locale].panels
  const panelTitle = a11yTitle ?? panelCopy[id].title
  const panelDescription = a11yDescription ?? panelCopy[id].description
  const panelDomId = `${id}-responsive-panel`
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const handleSheetOpenAutoFocus = useCallback(() => {
    if (typeof document === 'undefined') return
    const activeElement = document.activeElement
    returnFocusRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null
  }, [])

  const handleSheetCloseAutoFocus = useCallback((event: Event) => {
    const returnTarget = returnFocusRef.current
    returnFocusRef.current = null
    if (!returnTarget?.isConnected) return
    event.preventDefault()
    returnTarget.focus()
  }, [])

  const handleSheetEscapeKeyDown = useCallback((event: KeyboardEvent) => {
    // Escape confirms/cancels composition in Korean/CJK IMEs. Let the IME
    // consume that keystroke instead of dismissing the entire work panel.
    if (event.isComposing || event.keyCode === 229) event.preventDefault()
  }, [])

  if (gte(bp, inlineFrom)) {
    // Fixed inline mode. `open=false` still honored so the user's explicit
    // toggle action (e.g. top-bar chat close button) hides the panel even at
    // large viewports. Previously inline mode ignored `open`, making toggle
    // buttons silently no-op at xl+ — confusing UX.
    if (!open) return null
    const widthClass = widthByBreakpoint?.[bp]
    const hasPixelSize = typeof size === 'number' && size > 0
    const inlineStyle: CSSProperties | undefined = hasPixelSize
      ? side === 'bottom'
        ? { height: `${size}px` }
        : { width: `${size}px` }
      : undefined
    return (
      <div
        id={panelDomId}
        data-responsive-panel={id}
        className={cn(
          side === 'bottom'
            ? 'w-full relative shrink-0'
            : 'h-full relative shrink-0',
          !hasPixelSize && widthClass,
          className,
        )}
        style={inlineStyle}
      >
        {onResize && hasPixelSize && (
          <ResizeHandle
            id={id}
            side={side}
            size={size}
            onResize={onResize}
            resizeLabel={panelCopy[id].resize}
            formatPixels={panelCopy.pixels}
          />
        )}
        {children}
      </div>
    )
  }

  if (gte(bp, modalFrom)) {
    // Sheet slide-over mode
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          id={panelDomId}
          side={side}
          data-responsive-panel={id}
          className={cn(className, sheetClassName)}
          closeLabel={closeLabel ?? COOGO_IDE_CHROME_COPY[locale].close}
          onOpenAutoFocus={handleSheetOpenAutoFocus}
          onCloseAutoFocus={handleSheetCloseAutoFocus}
          onEscapeKeyDown={handleSheetEscapeKeyDown}
        >
          <SheetTitle className="sr-only">{panelTitle}</SheetTitle>
          <SheetDescription className="sr-only">{panelDescription}</SheetDescription>
          {children}
        </SheetContent>
      </Sheet>
    )
  }

  // bp < modalFrom — Sheet modal full height/width
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        id={panelDomId}
        side={side}
        data-responsive-panel={id}
        className={cn('w-full max-w-full', className, sheetClassName)}
        closeLabel={closeLabel ?? COOGO_IDE_CHROME_COPY[locale].close}
        onOpenAutoFocus={handleSheetOpenAutoFocus}
        onCloseAutoFocus={handleSheetCloseAutoFocus}
        onEscapeKeyDown={handleSheetEscapeKeyDown}
      >
        <SheetTitle className="sr-only">{panelTitle}</SheetTitle>
        <SheetDescription className="sr-only">{panelDescription}</SheetDescription>
        {children}
      </SheetContent>
    </Sheet>
  )
}

interface ResizeHandleProps {
  id: ResponsivePanelProps['id']
  side: 'left' | 'right' | 'bottom'
  size: number
  onResize: (next: number) => void
  /** Localized name for this handle — see F10 on ResponsivePanelProps. */
  resizeLabel: string
  /** Localized `aria-valuetext`; was a hardcoded English pixel count. */
  formatPixels: (size: number) => string
}

/**
 * Drag handle rendered on the interior edge of the panel. `side` describes
 * where the panel sits (e.g. `right` = inspector), so the handle itself
 * lives on the opposite edge (left border of the inspector, right border
 * of the chat, top border of the devtools dock). Pointer-events pinned to
 * the handle line; a full-viewport overlay is installed during an active
 * drag so the cursor stays in sync even when it slips off the 4px strip.
 */
function ResizeHandle({
  id,
  side,
  size,
  onResize,
  resizeLabel,
  formatPixels,
}: ResizeHandleProps) {
  const { min, max } = RESIZE_LIMITS[id]
  const boundedSize = Math.min(max, Math.max(min, size))
  const sizeRef = useRef(boundedSize)
  sizeRef.current = boundedSize
  const onResizeRef = useRef(onResize)
  onResizeRef.current = onResize

  const activePointerRef = useRef<{
    startClient: number
    startSize: number
  } | null>(null)

  const commitSize = useCallback(
    (next: number) => {
      const boundedNext = Math.min(max, Math.max(min, next))
      sizeRef.current = boundedNext
      onResizeRef.current(boundedNext)
    },
    [max, min],
  )

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const active = activePointerRef.current
      if (!active) return
      const client = side === 'bottom' ? e.clientY : e.clientX
      const delta = client - active.startClient
      // Panel side determines sign: dragging the handle away from the edge
      // that the panel is pinned to grows the panel.
      const sign = side === 'right' || side === 'bottom' ? -1 : 1
      const next = active.startSize + sign * delta
      commitSize(next)
    },
    [commitSize, side],
  )

  const endDrag = useCallback(() => {
    activePointerRef.current = null
    document.removeEventListener('pointermove', onPointerMove)
    document.removeEventListener('pointerup', endDrag)
    document.removeEventListener('pointercancel', endDrag)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [onPointerMove])

  useEffect(() => endDrag, [endDrag])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      activePointerRef.current = {
        startClient: side === 'bottom' ? e.clientY : e.clientX,
        startSize: sizeRef.current,
      }
      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', endDrag)
      document.addEventListener('pointercancel', endDrag)
      document.body.style.cursor = side === 'bottom' ? 'ns-resize' : 'ew-resize'
      document.body.style.userSelect = 'none'
    },
    [side, onPointerMove, endDrag],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null
      switch (event.key) {
        case 'Home':
          next = min
          break
        case 'End':
          next = max
          break
        case 'ArrowLeft':
          if (side !== 'bottom') {
            next =
              sizeRef.current + (side === 'right' ? RESIZE_STEP : -RESIZE_STEP)
          }
          break
        case 'ArrowRight':
          if (side !== 'bottom') {
            next =
              sizeRef.current + (side === 'left' ? RESIZE_STEP : -RESIZE_STEP)
          }
          break
        case 'ArrowUp':
          if (side === 'bottom') next = sizeRef.current + RESIZE_STEP
          break
        case 'ArrowDown':
          if (side === 'bottom') next = sizeRef.current - RESIZE_STEP
          break
      }
      if (next === null) return
      event.preventDefault()
      commitSize(next)
    },
    [commitSize, max, min, side],
  )

  // Offsets put the handle half outside the panel so the hit area spans
  // ~6px total while the visible strip stays 2px. Without the negative
  // offset, the handle lives entirely on the panel's interior edge and
  // users have to aim inside the panel to grab it.
  const positionClasses =
    side === 'right'
      ? '-left-[3px] top-0 h-full w-[6px] cursor-ew-resize'
      : side === 'left'
        ? '-right-[3px] top-0 h-full w-[6px] cursor-ew-resize'
        : '-top-[3px] left-0 w-full h-[6px] cursor-ns-resize'

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation={side === 'bottom' ? 'horizontal' : 'vertical'}
      aria-label={resizeLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={boundedSize}
      aria-valuetext={formatPixels(boundedSize)}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={cn(
        'absolute z-20 bg-transparent transition-colors hover:bg-primary/40 focus-visible:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        positionClasses,
      )}
    />
  )
}
